/**
 * 이상한 방 - 화면 위 오버레이가 서는 **자리의 규칙**
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 원칙 2(UI가 덮은 면적은 곧 볼 수 없는 방의 넓이)
 *           `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 대형 표시)
 *
 * ## 왜 생겼나
 *
 * 여기 오기 전까지 화면 위 오버레이 열 몇 개가 **손으로 맞춘 오프셋**으로 서로를 피했다.
 * `--ps-offset: 96px`(페이즈 블록 높이의 사본) · `--zp-top-clear: 148px`(그 위에 참가자 줄 높이를 더한 사본) ·
 * `--cb-bottom-clear: 76px`(HUD 하단 줄 높이의 사본) · `--dq-origin-width: 174px`(원래 방 버튼 폭의 사본).
 * 사본은 반드시 갈라진다 - 참가자가 여덟이면 줄이 두세 겹이 되어 진행판을 덮었고, 실제로 덮었다
 * (`__weirdRoom.probeOverlaps()`가 데스크톱 1280에서도 12,600px²를 셌다).
 *
 * 픽셀을 늘려 덮는 것은 다음 화면에서 또 깨진다. 그래서 **자리를 규칙으로** 바꾼다.
 *
 * ## 규칙 둘
 *
 * ### 1. 위쪽은 하나의 흐름이다 (`.ol-stack`)
 *
 * HUD 상단 줄 아래에서 시작해 **위에서 아래로 쌓인다**. 오프셋이 없고, 앞의 것이 커지면 뒤의 것이 밀린다.
 *
 * ```
 *   [HUD 상단 줄: 페이즈 시계 · 발견 카운터 · 설정 톱니]      ← .gh-top (HUD 루트 안, 옮길 수 없다)
 *   ── .ol-stack 시작 ────────────────────────────────
 *   [왼쪽 열]  참가자 줄 → 구획 진행판                      ← .ol-stack__left  (왼쪽 정렬)
 *   [가운데]   HUD 자막 → 리빌 이름표 → 걸기 배너            ← .ol-stack__top   (가운데 정렬)
 * ```
 *
 * 왼쪽 열이 **먼저**인 이유: 가운데 알림은 1~2초짜리 손님이고 왼쪽 열은 판 내내 서 있다. 알림을 위에 두면
 * 알림이 뜰 때마다 참가자 줄이 아래로 뛴다. 곁눈질로 읽는 줄이 뛰면 그 줄은 못 읽는다(`playerStrip.css` 머리말).
 * 순서를 뒤집으면 위의 것은 절대 움직이지 않고 알림만 아래에 붙었다 사라진다.
 *
 * ### 2. 아래쪽은 소유자가 내보낸 높이로 쌓는다
 *
 * 아래는 흐름으로 묶을 수 없다 - 구성원 중 하나(HUD 하단 줄)가 HUD 루트 **안**에 살아서 이 컨테이너로
 * 옮길 수 없기 때문이다. 그래서 대신 **자기 크기를 CSS 변수로 내보내고**(`publishSize`), 위에 서는 쪽이
 * 그 변수를 읽는다. 사본을 만들지 않는다는 규칙은 그대로다 - 값의 원본은 언제나 그 상자를 소유한 파일이다.
 *
 * ```
 *   [카메라 묶음]              ← .mc-cluster   : max(하단 줄, 시점 줄) + 바꾸기 띠 만큼 올라선다
 *   [바꾸기·꾸미기 띠 / 시점 줄]  ← .cb-root .db-root .sb-root
 *   [HUD 하단 줄: 잔탄 · 탄창 · 원래 방]  ← .gh-bottom
 * ```
 *
 * ## 연결 배너는 이 기둥 밖에 산다
 *
 * `.cn-root`만 예외다. 설정 패널(47)보다 위(48)여야 하는데(끊긴 것은 패널이 열려 있어도 보여야 한다),
 * 이 기둥은 z-index 22라 그 안에 넣으면 그 성질을 잃는다. 대신 배너가 **자기 높이를 내보내고**
 * (`--cn-height`) 기둥이 그만큼 내려간다 - 자리는 흐름과 똑같이 정해지고 층만 다르다.
 *
 * ## 이 파일은 규칙을 모른다
 *
 * 무엇이 언제 보이는지는 각 모듈이 정한다. 여기 있는 것은 **어디에 서는가**뿐이다.
 */

import { onLocaleChange } from '../i18n'

import './overlayLayout.css'

/** 기둥 안의 자리. `lane`이 어느 기둥인지, `order`가 그 안의 순서다(작을수록 위). */
export type OverlaySlotDef = Readonly<{ lane: 'left' | 'top'; order: number }>

/**
 * 자리표. **새 오버레이는 반드시 여기에 이름을 얻고 들어온다** - 표에 없으면 `position: fixed`로
 * 혼자 서게 되고, 그 순간 다시 손으로 맞춘 오프셋이 하나 생긴다.
 */
export const OVERLAY_SLOTS = {
  /** 참가자 줄(`playerStrip`). 왼쪽 열의 맨 위 - 판 내내 서 있는 것이 위다. */
  players: { lane: 'left', order: 10 },
  /** 구획 진행판(`zoneProgress`). 참가자 줄 바로 아래 - 같은 사람들의 이름이 위아래로 이어져야 한다. */
  zones: { lane: 'left', order: 20 },
  /** HUD 자막(`gameHud`의 `.gh-notice`). 가운데 기둥의 맨 위 - "왜 안 되는가"를 말하는 줄이라 가장 급하다. */
  notice: { lane: 'top', order: 10 },
  /** 리빌 이름표(`revealParty`). */
  reveal: { lane: 'top', order: 20 },
  /** 걸기 배너(`wagerBanner`). */
  wager: { lane: 'top', order: 30 },
  /**
   * 여럿이 첫 바꾸기 안내(`changerCoach`). 알림 열의 **맨 아래**다 - 위 셋은 지금 일어난 일을 말하고
   * 이것은 다음에 할 일을 말한다. 급한 순서가 곧 위아래 순서다.
   */
  coach: { lane: 'top', order: 40 },
} as const satisfies Record<string, OverlaySlotDef>

export type OverlaySlot = keyof typeof OVERLAY_SLOTS

export type OverlayLayout = Readonly<{
  /** 기둥 전체. 테스트가 구조를 확인할 때만 쓴다. */
  root: HTMLElement
  /** 상자를 자리에 세운다. 이미 다른 자리에 있었으면 옮긴다. */
  place: (el: HTMLElement, slot: OverlaySlot) => void
  /**
   * 상자를 걷는다. **`dispose()`에서 반드시 부른다** - `el.remove()`만 하면 관측기가 떼어 낸 노드를 계속
   * 붙들고 있다(`ResizeObserver`는 대상을 강하게 참조하고, 기둥은 `document.body`에 걸려 앱 수명 내내 하나다).
   * 무대는 단계마다 다시 서므로 그 누수는 판마다 한 벌씩 쌓인다(`ui/mobileControls.ts`의 같은 규약).
   */
  unplace: (el: HTMLElement) => void
  /** 열이 잘렸는지 다시 판정한다. 칸의 내용이 바뀐 것을 아는 쪽이 부른다. */
  syncClip: () => void
}>

/**
 * 마운트 하나에 기둥 하나. 여러 모듈이 같은 `mount`로 들어오므로 **부를 때마다 만들면 기둥이 여러 개**가 되고,
 * 그러면 서로를 피하지 못한다(그게 정확히 이 파일이 없애려는 실패다).
 */
const STACKS = new WeakMap<HTMLElement, OverlayLayout>()

function createStack(mount: HTMLElement): OverlayLayout {
  const root = document.createElement('div')
  root.className = 'ol-stack'
  // 기둥 자체는 탭을 삼키지 않는다. 실제로 누를 것이 있는 상자만 스스로 되살린다.
  root.setAttribute('aria-hidden', 'false')

  const left = document.createElement('div')
  left.className = 'ol-stack__left'
  const top = document.createElement('div')
  top.className = 'ol-stack__top'

  root.append(left, top)
  mount.append(root)

  /*
   * 열이 잘렸는가. **두 열 모두** 본다.
   *
   * 좁은 화면에서는 위 기둥이 아래 기둥에 닿기 전에 멈추고 넘치는 만큼을 자른다(`overlayLayout.css`).
   * 먼저 줄어드는 것은 왼쪽 열이고, 그것이 0이 되고도 모자라면 알림 열의 끝이 잘린다.
   * 자른 자리가 **직선으로 뚝 끊기면 고장 난 화면으로 읽힌다** - 목록의 끝이 흐려져야 "더 있다"가 전달된다
   * (`gameHud.css`의 `.gh-fade-y`가 단계 선택에서 쓰는 것과 같은 신호).
   *
   * 그런데 그 흐림을 늘 걸어 두면 **안 잘렸을 때도 마지막 칸이 흐려진다.** 그래서 실제로 넘칠 때만 건다.
   * 이 판단이 늦거나 아예 안 와도(배경 탭) 겹침은 생기지 않는다 - 잘림은 그대로고 흐림만 없다.
   */
  const syncClip = (): void => {
    for (const lane of [left, top]) {
      lane.classList.toggle('is-clipped', lane.scrollHeight > lane.clientHeight + 1)
    }
  }

  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(syncClip) : null
  observer?.observe(left)
  observer?.observe(top)
  window.addEventListener('resize', syncClip)

  return {
    root,
    place(el: HTMLElement, slot: OverlaySlot): void {
      const def = OVERLAY_SLOTS[slot]
      const lane = def.lane === 'left' ? left : top
      el.style.order = String(def.order)
      lane.append(el)
      // 칸의 크기가 바뀌면 잘림도 바뀐다. 칸 자신을 관측해 두면 줄 수가 늘어나는 순간을 놓치지 않는다.
      observer?.observe(el)
      syncClip()
    },
    unplace(el: HTMLElement): void {
      // 관측을 먼저 끊는다. 노드를 떼는 것만으로는 안 풀린다 - 문서 밖으로 나가도 관측 목록에는 그대로 남는다.
      observer?.unobserve(el)
      el.remove()
      syncClip()
    },
    syncClip,
  }
}

/** 이 마운트의 기둥. 없으면 만든다. 기둥이 마운트에서 떨어져 나갔으면 다시 붙인다(무대는 여러 번 다시 선다). */
export function overlayLayout(mount: HTMLElement): OverlayLayout {
  const found = STACKS.get(mount)
  if (found !== undefined) {
    if (found.root.parentNode !== mount) mount.append(found.root)
    return found
  }
  const made = createStack(mount)
  STACKS.set(mount, made)
  return made
}

/* --- 크기 내보내기 --------------------------------------------------------------- */

/** 어느 축을 내보낼 것인가. 값은 CSS 변수 이름이다(`--gh-top-height` 꼴). */
export type SizeExport = Readonly<{
  width?: string
  height?: string
  /**
   * 이 상자가 **지금 자리를 차지하는가**. 없으면 언제나 참이다.
   *
   * `[hidden]`으로 접히는 상자는 사각형이 이미 0이라 이 문이 필요 없다. 필요한 것은 연결 배너처럼
   * **접히지 않고 투명해지기만 하는** 상자다 - 그런 상자는 사각형이 그대로라, 안 보이는데도 남의 자리를 밀어낸다.
   */
  visible?: () => boolean
}>

/**
 * 상자가 **자기 크기를 CSS 변수로 내보낸다**. 남의 파일이 그 폭을 손으로 베끼지 않게 하는 유일한 길이다
 * (`--dq-origin-width: 174px`가 정확히 그 사본이었다. 글꼴이나 언어가 바뀌면 바로 어긋난다).
 *
 * 변수는 `document.documentElement`에 쓴다. 읽는 쪽이 형제(다른 루트)라 상속으로는 닿지 않기 때문이다.
 *
 * ## 되먹임이 없는 이유
 *
 * 내보낸 값을 읽는 쪽은 **자기 자리**만 옮긴다(`bottom`·`right`·`top`). 관측 대상의 크기는 그 자리에
 * 종속되지 않으므로 관측 → 쓰기 → 재관측의 고리가 닫히지 않는다. 그래도 반올림한 값이 같으면 쓰지 않는다 -
 * 소수점 떨림이 매 프레임 스타일 무효화를 부르는 것을 막는다.
 *
 * `ResizeObserver`가 없는 환경(jsdom)에서도 던지지 않는다. 자리 계산은 전부 CSS의 폴백 값으로 성립하므로
 * (모든 `var()`에 폴백이 있다) 관측기가 없다고 화면이 깨지지는 않는다.
 */
export function publishSize(el: HTMLElement, vars: SizeExport): SizePublisher {
  const target = document.documentElement
  const last = new Map<string, number>()

  /*
   * **올림이지 반올림이 아니다.** 이 값을 읽는 쪽은 비키는 쪽이라(`--ol-bottom-clear` → 카메라 묶음의 `--hud-lift`)
   * 모자라면 그만큼 겹친다 - 실측 66.2px짜리 띠를 66으로 내보냈더니 카메라 묶음이 0.2px 얹혔고, 그것이
   * `probeOverlaps().pairs`의 35px²였다. 0.8px 남는 것은 눈에 보이지 않지만 0.2px 겹치는 것은 보고서에 남는다.
   *
   * 올린 값이 같으면 쓰지 않는다 - 소수점 떨림이 매 프레임 스타일 무효화를 부르는 것을 막는다.
   *
   * 단, **문서에 실제로 값이 있을 때만** 그 단축이 성립한다. 같은 이름을 쓰는 퍼블리셔가 둘 있을 때
   * (새 인스턴스를 먼저 세우고 옛 인스턴스를 나중에 버리는 순서) `clearSize`가 문서에서 값을 통째로 지우는데,
   * 살아남은 쪽의 기억에는 값이 남아 있어 `refresh()`를 몇 번 불러도 다시 쓰지 않았다 - CSS가 영원히
   * 폴백(`--gh-bottom-height, 67px`)을 읽는다. 지금 그 순서가 안 나는 이유는 `main.ts`의 무대 교체가
   * "먼저 버리고 나중에 세운다"뿐인데, 그 순서를 이 파일이 요구하지 않으므로 여기서 스스로 막는다.
   * 인라인 스타일을 읽는 것은 계산 스타일과 달리 레이아웃을 부르지 않는다.
   */
  const write = (name: string, value: number): void => {
    const rounded = Math.ceil(value)
    if (last.get(name) === rounded && target.style.getPropertyValue(name) !== '') return
    last.set(name, rounded)
    target.style.setProperty(name, `${rounded}px`)
  }

  const measure = (): void => {
    const shows = vars.visible === undefined || vars.visible()
    const rect = shows ? el.getBoundingClientRect() : null
    if (vars.width !== undefined) write(vars.width, rect?.width ?? 0)
    if (vars.height !== undefined) write(vars.height, rect?.height ?? 0)
  }

  measure()

  /*
   * 재는 계기가 셋인 이유.
   *
   * `ResizeObserver`만으로는 부족하다. 그 콜백은 브라우저의 렌더링 갱신 단계에서 돌아서 **배경 탭에서는
   * 아예 오지 않고**(이 저장소의 자동 검증이 정확히 그 환경이다 - `debugHooks.ts`가 rAF를 안 쓰는 이유와 같다),
   * `display: none`이 된 요소에 0을 알려 주는지도 브라우저마다 다르다.
   *
   * 그래서 뷰포트와 언어는 여기서 직접 듣고(둘 다 상자의 크기를 바꾸는 사건이다), 나머지 -
   * 접었다 폈다·줄 수가 바뀌었다 같은 것 - 는 그 사실을 아는 쪽이 `refresh()`로 알린다.
   */
  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null
  observer?.observe(el)
  const stopLocaleWatch = onLocaleChange(measure)
  window.addEventListener('resize', measure)

  return {
    refresh: measure,
    dispose(): void {
      observer?.disconnect()
      stopLocaleWatch()
      window.removeEventListener('resize', measure)
      clearSize(vars)
      // 기억도 함께 버린다. 이 인스턴스가 다시 쓰일 일은 없지만, 남겨 두면 "무엇을 썼는가"가 거짓으로 남는다.
      last.clear()
    },
  }
}

export type SizePublisher = Readonly<{
  /** 지금 다시 재고 내보낸다. 상자를 접거나 편 직후처럼 관측기가 놓칠 수 있는 순간에 부른다. */
  refresh: () => void
  /** 관측을 멈추고 내보낸 값을 지운다. */
  dispose: () => void
}>

/**
 * 내보낸 값을 지운다. **떠난 상자의 높이가 남아 있으면 그만큼이 영원히 빈칸이 된다** -
 * 바꾸기 띠가 사라졌는데 `--cb-height`가 150px로 남으면 카메라 묶음이 화면 한가운데에 뜬다.
 */
function clearSize(vars: SizeExport): void {
  const target = document.documentElement
  if (vars.width !== undefined) target.style.removeProperty(vars.width)
  if (vars.height !== undefined) target.style.removeProperty(vars.height)
}
